import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { MapPin, Phone, Globe, Clock, Mail, Store, Navigation2, MessageCircle, ArrowRight, Star, Newspaper, Sparkles, Users, FileText } from "lucide-react";
import OfferCard from "@/components/OfferCard";
import BusinessReviews from "@/components/BusinessReviews";
import FavoriteButton from "@/components/FavoriteButton";
import ShareButton from "@/components/ShareButton";
import RecommendButton from "@/components/RecommendButton";
import BusinessGallery from "@/components/BusinessGallery";
import BusinessVideos from "@/components/BusinessVideos";
import { Button } from "@/components/ui/button";
import { useGeolocation } from "@/hooks/useGeolocation";
import { useSeo } from "@/hooks/useSeo";
import { buildKeywords } from "@/components/KeywordsSuggestBlock";
import { SuggestLink } from "@/components/SuggestLink";
import BreadcrumbJsonLd from "@/components/BreadcrumbJsonLd";

function haversineKm(a, b) {
  if (!a || !b) return null;
  const r = 6371, p = Math.PI / 180;
  const d = 0.5 - Math.cos((b.lat - a.lat) * p) / 2
          + Math.cos(a.lat * p) * Math.cos(b.lat * p) * (1 - Math.cos((b.lng - a.lng) * p)) / 2;
  return Math.round(2 * r * Math.asin(Math.sqrt(d)) * 10) / 10;
}

export default function BusinessDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const geo = useGeolocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/businesses/${id}`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [id]);

  const distance = useMemo(() => {
    if (!data || !geo.coords) return null;
    const b = data.business;
    if (!b.lat || !b.lng) return null;
    return haversineKm(geo.coords, { lat: Number(b.lat), lng: Number(b.lng) });
  }, [data, geo.coords]);

  const seoBiz = data && data.business;
  useSeo({
    title: seoBiz ? `${seoBiz.name}${seoBiz.city ? ` à ${seoBiz.city}` : ""} — Avis, offres & infos | PROXIreduc-28` : undefined,
    description: seoBiz
      ? `${seoBiz.name}${seoBiz.category?.name ? `, ${seoBiz.category.name}` : ""}${seoBiz.city ? ` à ${seoBiz.city}` : ""} en Eure-et-Loir : coordonnées, horaires, avis clients et bons plans. Découvrez cette adresse locale sur PROXIreduc-28.`
      : undefined,
    canonicalPath: seoBiz ? `/commerce/${seoBiz.id}` : undefined,
    image: seoBiz ? (seoBiz.cover_image || (seoBiz.gallery && seoBiz.gallery[0]) || "/og-commerces.jpg") : "/og-commerces.jpg",
    keywords: seoBiz ? buildKeywords(seoBiz.category?.name, seoBiz.city).join(", ") : undefined,
  });

  if (data === null) return <div className="p-10 text-center text-slate-500" data-testid="biz-loading">Chargement…</div>;
  if (data === false) return <div className="p-10 text-center text-slate-500" data-testid="biz-notfound">Commerce introuvable.</div>;
  if (data && data.pending_validation) return (
    <div className="max-w-xl mx-auto px-4 py-24 text-center" data-testid="biz-pending-validation">
      <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-amber-600 bg-amber-50 px-3 py-1.5 rounded-full mb-5">PROXIreduc-28</div>
      <h1 className="text-2xl font-bold text-slate-900 mb-3">Fiche en cours de validation</h1>
      <p className="text-slate-500 leading-relaxed">Cette fiche n'est pas encore en ligne. Elle sera publiée dès qu'elle aura été vérifiée et validée par notre équipe.</p>
    </div>
  );
  const { business, offers, similar = [], related_articles = [] } = data;

  const gallery = (business.gallery && business.gallery.length) ? business.gallery
    : (business.images && business.images.length) ? business.images : [];
  const videos = business.videos || [];
  const heroImg = business.cover_image || gallery[0] || "";

  const isNew = (() => {
    if (!business.created_at) return false;
    const days = (Date.now() - new Date(business.created_at).getTime()) / 86400000;
    return days >= 0 && days < 30;
  })();

  const trackShare = (ch) => { api.post(`/businesses/${business.id}/share?channel=${ch}`).catch(() => {}); };
  const vitrineUrl = `${process.env.REACT_APP_BACKEND_URL}/api/vitrine/${business.id}`;

  const ldJson = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: business.name,
    ...(business.description && { description: business.description }),
    ...(heroImg && { image: heroImg }),
    ...(business.phone && { telephone: business.phone }),
    ...(business.website && { url: business.website }),
    address: {
      "@type": "PostalAddress",
      ...(business.address && { streetAddress: business.address }),
      ...(business.postal_code && { postalCode: business.postal_code }),
      ...(business.city && { addressLocality: business.city }),
      addressCountry: "FR",
    },
    ...(business.lat && business.lng && {
      geo: { "@type": "GeoCoordinates", latitude: business.lat, longitude: business.lng },
    }),
  };

  const whatsappShare = () => {
    const msg = `J'ai trouvé ce commerce près de chez nous sur PROXIreduc-28 👇\n${business.name} — ${business.city}\n${vitrineUrl}`;
    trackShare("whatsapp");
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer");
  };

  const itineraryUrl = business.address || business.lat
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
        business.lat && business.lng
          ? `${business.lat},${business.lng}`
          : `${business.address || ""} ${business.postal_code || ""} ${business.city || ""}`.trim()
      )}`
    : null;

  return (
    <div className="bg-slate-50 min-h-screen">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(ldJson) }} />
      <BreadcrumbJsonLd items={[{ name: "Accueil", path: "/" }, { name: "Partenaires", path: "/partenaires" }, { name: business.name }]} />
      {/* ===== HERO ===== */}
      <div className="relative h-72 sm:h-96 lg:h-[420px] bg-slate-800 overflow-hidden" data-testid="biz-cover">
        {heroImg
          ? <img src={heroImg} alt={business.name} className="w-full h-full object-cover" />
          : <div className="w-full h-full grid place-items-center text-slate-500"><Store size={64} /></div>}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-900/40 to-black/20" />

        <div className="absolute bottom-0 left-0 right-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6 sm:pb-8 text-white flex items-end justify-between gap-4">
            <div className="flex items-end gap-4 min-w-0">
              {business.logo && (
                <div className="w-20 h-20 sm:w-28 sm:h-28 rounded-2xl border-4 border-white shadow-2xl bg-white overflow-hidden ring-2 ring-slate-900/10 shrink-0">
                  <img src={business.logo} alt={`Logo ${business.name}`} className="w-full h-full object-cover" />
                </div>
              )}
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  {business.category?.name && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-widest bg-gradient-to-r from-amber-400 to-[#E05638] text-white shadow">
                      {business.category.name}
                    </span>
                  )}
                  {isNew && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-[#E05638] text-white shadow" data-testid="biz-new-badge">
                      <Sparkles size={11} /> Nouveau
                    </span>
                  )}
                  {business.network?.is_member && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-sky-600 text-white shadow" data-testid="biz-network-badge" title="Ce commerçant fait partie du Réseau Pro local d'Eure-et-Loir">
                      <Users size={11} /> Membre du Réseau Pro du 28{business.network.reco_count > 0 ? ` · ${business.network.reco_count} reco` : ""}
                    </span>
                  )}
                </div>
                <h1 className="mt-1.5 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight drop-shadow-sm" data-testid="business-name">
                  {business.name}
                </h1>
                <div className="mt-2 flex flex-wrap items-center gap-2.5 text-sm">
                  <span className="inline-flex items-center gap-1 text-white/90"><MapPin size={14} /> {business.city}</span>
                  {distance != null && (
                    <span className="relative inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-emerald-500 text-white font-bold" data-testid="biz-distance">
                      <span className="absolute -left-0.5 -top-0.5 w-2.5 h-2.5 rounded-full bg-emerald-300 animate-ping" />
                      <Navigation2 size={12} /> à {distance} km de vous
                    </span>
                  )}
                </div>
              </div>
            </div>
            <FavoriteButton type="business" id={business.id} size="lg" className="shrink-0" />
          </div>
        </div>
      </div>

      {/* ===== STICKY ACTION BAR ===== */}
      <div className="sticky top-16 z-30 bg-white/85 backdrop-blur-md border-y border-slate-200/80 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-2 overflow-x-auto scrollbar-none lg:flex-wrap lg:overflow-visible" data-testid="biz-actions">
          <RecommendButton businessId={business.id} />
          {business.phone && (
            <a href={`tel:${business.phone}`} data-testid="biz-call" className="shrink-0">
              <Button variant="outline" className="h-10"><Phone size={14} className="mr-1.5" /> Appeler</Button>
            </a>
          )}
          {itineraryUrl && (
            <a href={itineraryUrl} target="_blank" rel="noreferrer" data-testid="biz-itinerary" className="shrink-0">
              <Button variant="outline" className="h-10"><Navigation2 size={14} className="mr-1.5" /> Itinéraire</Button>
            </a>
          )}
          <Button onClick={whatsappShare} variant="outline" className="h-10 shrink-0 text-green-700 border-green-300 hover:bg-green-50" data-testid="biz-whatsapp">
            <MessageCircle size={14} className="mr-1.5" /> WhatsApp
          </Button>
          <div className="shrink-0">
            <ShareButton url={vitrineUrl} title={business.name}
                         text={`Découvrez ${business.name} sur PROXIreduc-28 👇`} testId="biz-share" onShare={trackShare} />
          </div>
          <Link to={`/demande-de-devis?pro=${business.id}`} data-testid="biz-request-quote" className="shrink-0">
            <Button className="h-10 bg-[#E05638] hover:bg-[#c7482d] text-white"><FileText size={14} className="mr-1.5" /> Demander un devis</Button>
          </Link>
          <a href={vitrineUrl} target="_blank" rel="noreferrer" data-testid="biz-google-page" className="shrink-0">
            <Button variant="outline" className="h-10 text-emerald-700 border-emerald-300 hover:bg-emerald-50">
              <Globe size={14} className="mr-1.5" /> Voir le mini-site
            </Button>
          </a>
          {business.google_review_url && (
            <a href={business.google_review_url} target="_blank" rel="noreferrer" data-testid="biz-google-review" className="shrink-0">
              <Button variant="outline" className="h-10 text-amber-700 border-amber-300 hover:bg-amber-50">
                <Star size={14} className="mr-1.5 fill-amber-500 text-amber-500" /> Laisser un avis Google
              </Button>
            </a>
          )}
          {!geo.coords && business.lat && business.lng && (
            <Button onClick={() => geo.request()} variant="outline" className="h-10 shrink-0 text-emerald-700 border-emerald-300 hover:bg-emerald-50" data-testid="biz-geo-ask">
              <Navigation2 size={14} className="mr-1.5" /> {geo.loading ? "Localisation..." : "Afficher la distance"}
            </Button>
          )}
        </div>
      </div>

      {/* ===== BODY ===== */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
        <div className="lg:col-span-8 space-y-10">
          <BusinessGallery images={gallery} name={business.name} />

          <BusinessVideos videos={videos} />

          {/* À propos */}
          <section>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">À propos</h2>
            <p className="mt-3 text-base sm:text-lg text-slate-600 leading-relaxed whitespace-pre-line">
              {business.description || "Description à venir."}
            </p>
            <SuggestLink />
            <div className="mt-4 flex flex-wrap gap-2">
              {business.category?.name && (
                <span className="px-3 py-1 rounded-full bg-orange-50 text-[#c8462b] text-xs font-semibold border border-orange-100">{business.category.name}</span>
              )}
              {business.subcategory?.name && (
                <span className="px-3 py-1 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold">{business.subcategory.name}</span>
              )}
              {business.coverage === "department" && (
                <span className="px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold border border-emerald-100">Tout l'Eure-et-Loir</span>
              )}
            </div>
          </section>

          {/* Offres */}
          <section>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-4">Les offres de ce partenaire</h2>
            {offers.length ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {offers.map((o) => (
                  <OfferCard key={o.id} offer={{ ...o, business: { id: business.id, name: business.name, city: business.city }, category: business.category }} />
                ))}
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-8 text-center text-slate-500">
                Aucune offre active pour le moment.
              </div>
            )}
          </section>

          {/* Avis */}
          <BusinessReviews businessId={business.id} businessName={business.name} />

          {/* Articles liés */}
          {related_articles.length > 0 && (
            <section data-testid="biz-articles">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-4 flex items-center gap-2">
                <Newspaper size={22} className="text-[#E05638]" /> Articles qui parlent de {business.category?.name || business.name} à {business.city}
              </h2>
              <div className="grid grid-cols-1 gap-3" data-testid="biz-articles-list">
                {related_articles.map((a) => (
                  <Link key={a.id} to={`/blog/${a.slug}`} data-testid={`biz-article-${a.id}`}
                        className="group flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-2.5 hover:bg-slate-50 transition-colors">
                    <div className="w-14 h-14 shrink-0 rounded-lg overflow-hidden bg-slate-100">
                      {a.cover_image ? <img src={a.cover_image} alt={a.title} className="w-full h-full object-cover" loading="lazy" /> :
                        <div className="w-full h-full grid place-items-center text-slate-300"><Newspaper size={18} /></div>}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-slate-900 text-sm line-clamp-2">{a.title}</div>
                      <div className="mt-0.5 inline-flex items-center gap-1 text-[#E05638] font-semibold text-xs">Lire l'article <ArrowRight size={11} /></div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Similaires */}
          {similar.length > 0 && (
            <section data-testid="biz-similar">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-4">Vous pourriez également aimer</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {similar.map((s) => (
                  <Link key={s.id} to={`/commerce/${s.id}`} data-testid={`biz-similar-${s.id}`}
                        className="card-hover group bg-white rounded-2xl border border-slate-200 overflow-hidden">
                    <div className="aspect-[4/3] bg-slate-100">
                      {s.cover_image ? <img src={s.cover_image} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" /> :
                        <div className="w-full h-full grid place-items-center text-slate-300"><Store size={40} /></div>}
                    </div>
                    <div className="p-4">
                      <div className="font-heading font-semibold text-slate-900 line-clamp-1">{s.name}</div>
                      <div className="mt-1 text-xs text-slate-500 flex items-center gap-1"><MapPin size={11} /> {s.city}</div>
                      <div className="mt-2 inline-flex items-center gap-1 text-[#E05638] font-semibold text-xs opacity-0 group-hover:opacity-100 transition-opacity">Voir le commerce <ArrowRight size={11} /></div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* ===== SIDEBAR ===== */}
        <aside className="lg:col-span-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-3.5 h-fit lg:sticky lg:top-32 shadow-sm" data-testid="biz-sidebar">
            <div className="font-heading font-bold text-lg text-slate-900">Coordonnées</div>
            {business.address && (
              <div className="flex gap-2.5 text-sm text-slate-700">
                <MapPin size={16} className="mt-0.5 shrink-0 text-[#E05638]" />
                <span>{business.address}{business.postal_code ? `, ${business.postal_code}` : ""} {business.city}</span>
              </div>
            )}
            {business.phone && (
              <a href={`tel:${business.phone}`} className="flex gap-2.5 text-sm text-slate-700 hover:text-[#E05638] transition-colors">
                <Phone size={16} className="mt-0.5 shrink-0 text-[#E05638]" /> {business.phone}
              </a>
            )}
            {business.email && (
              <a href={`mailto:${business.email}`} className="flex gap-2.5 text-sm text-slate-700 hover:text-[#E05638] transition-colors">
                <Mail size={16} className="mt-0.5 shrink-0 text-[#E05638]" /> <span className="truncate">{business.email}</span>
              </a>
            )}
            {business.website && (
              <a href={business.website.startsWith("http") ? business.website : `https://${business.website}`}
                 target="_blank" rel="noreferrer" className="flex gap-2.5 text-sm text-slate-700 hover:text-[#E05638] transition-colors">
                <Globe size={16} className="mt-0.5 shrink-0 text-[#E05638]" />
                <span className="truncate">{business.website.replace(/^https?:\/\//, "")}</span>
              </a>
            )}
            {business.opening_hours && (
              <div className="flex gap-2.5 text-sm text-slate-700 pt-1 border-t border-slate-100">
                <Clock size={16} className="mt-0.5 shrink-0 text-[#E05638]" />
                <div>
                  <div className="font-semibold text-slate-900 text-xs uppercase tracking-wide mb-0.5">Horaires</div>
                  <span className="whitespace-pre-line">{business.opening_hours}</span>
                </div>
              </div>
            )}
            {itineraryUrl && (
              <a href={itineraryUrl} target="_blank" rel="noreferrer" className="block">
                <Button className="w-full btn-shine text-white"><Navigation2 size={14} className="mr-1.5" /> Y aller</Button>
              </a>
            )}
            {distance != null && (
              <div className="pt-2 border-t border-slate-100 inline-flex items-center gap-2 text-sm font-semibold text-emerald-700">
                <Navigation2 size={14} /> à {distance} km de vous
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
